// Activity: what needs you, across every org. Mentions first (with the
// line that mentioned you), then conversations waiting on you: unread
// DMs and channels where you were mentioned. It reads the same room
// counts Home shows, so a locked (not yet decryptable) message still
// counts; it doesn't have to render.

import { useMemo, useState } from "react";

import { useMobileStatus } from "~/components/mobile/status";
import { mobileStrings } from "~/components/mobile/strings";
import { formatRoomTime } from "~/components/mobile/time";
import { useMobileMentions, useMobileUnreads } from "~/hooks/useMobileActivity";
import { useThreadsFeed } from "~/hooks/useThreadsFeed";
import { useActivityFeed, type ActivityRow } from "~/hooks/useAttention";

export interface MobileActivityProps {
  onOpenRoom: (roomId: string) => void;
}

type Filter = "all" | "mentions" | "threads";

export function MobileActivity({ onOpenRoom }: MobileActivityProps) {
  const rows = useActivityFeed();
  const { clearAll: clearMentions } = useMobileMentions();
  const { markAllRead: markUnreadsRead } = useMobileUnreads();
  const { markAllRead: markThreadsRead } = useThreadsFeed();
  const [filter, setFilter] = useState<Filter>("all");
  const [clearing, setClearing] = useState(false);
  const status = useMobileStatus();

  const visible = useMemo(() => {
    if (filter === "mentions") return rows.filter((row) => row.kind === "mention");
    if (filter === "threads") return rows.filter((row) => row.kind === "thread");
    return rows;
  }, [rows, filter]);

  const handleClearAll = async () => {
    setClearing(true);
    try {
      await Promise.all([clearMentions(), markUnreadsRead(), markThreadsRead()]);
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-14 shrink-0 items-center justify-between gap-3 px-4">
        <h1 className="text-base font-light text-foreground/90">{mobileStrings.activity.title}</h1>
        {status && (
          <span aria-hidden className="truncate text-xs font-light text-muted-foreground">
            {status}
          </span>
        )}
      </div>
      {rows.length === 0 ? (
        <ActivityEmpty />
      ) : (
        <>
          <FilterChips filter={filter} onChange={setFilter} />
          {visible.length === 0 ? (
            <div className="flex-1 px-4 py-6">
              <p className="text-sm font-light text-muted-foreground">
                {mobileStrings.activity.empty}
              </p>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pb-4">
              <ul className="flex flex-col">
                {visible.map((row) => (
                  <ActivityRowItem key={row.id} row={row} onOpen={() => onOpenRoom(row.roomId)} />
                ))}
              </ul>
              <button
                type="button"
                onClick={() => void handleClearAll()}
                disabled={clearing}
                className="mx-4 mt-4 h-11 self-start text-xs font-light text-muted-quiet outline-none transition-opacity hover:text-foreground/70 focus-visible:shadow-neon-cyan disabled:text-foreground/15"
              >
                {mobileStrings.activity.markAllRead}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function chipStyle(active: boolean): string {
  const cls = [
    "rounded-full",
    "px-3",
    "py-1",
    "text-xs",
    "font-light",
    "outline-none",
    "transition-opacity",
    "focus-visible:shadow-neon-cyan",
  ].join(" ");
  return active
    ? `${cls} bg-foreground/[0.12] text-foreground/90`
    : `${cls} text-muted-quiet hover:text-foreground/70`;
}

function FilterChips({ filter, onChange }: { filter: Filter; onChange: (next: Filter) => void }) {
  const options: Filter[] = ["all", "mentions", "threads"];
  const label = (option: Filter) =>
    option === "all"
      ? mobileStrings.activity.filterAll
      : option === "mentions"
        ? mobileStrings.activity.mentions
        : mobileStrings.activity.threads;
  return (
    <div
      role="group"
      aria-label={mobileStrings.activity.filtersLabel}
      className="flex gap-2 px-4 pb-2"
    >
      {options.map((option) => (
        <button
          key={option}
          type="button"
          aria-pressed={filter === option}
          onClick={() => onChange(option)}
          className={chipStyle(filter === option)}
        >
          {label(option)}
        </button>
      ))}
    </div>
  );
}

function ActivityRowItem({ row, onOpen }: { row: ActivityRow; onOpen: () => void }) {
  let header: string;
  let body: string | null;
  if (row.kind === "thread") {
    header = mobileStrings.activity.repliedIn(row.sender);
    body = row.preview.length > 0 ? row.preview : null;
  } else {
    const who = row.sender.displayName || row.sender.id;
    header = `${row.roomName} · ${who}`;
    body = row.kind === "reaction" ? mobileStrings.activity.reactedWith(row.emoji) : row.body;
  }
  const ariaLabel =
    row.kind === "thread"
      ? `${header} (${row.roomName})${body ? `: ${body}` : ""}`
      : `${header}${body ? `: ${body}` : ""}`;
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        aria-label={ariaLabel}
        className="flex w-full flex-col gap-0.5 px-4 py-2.5 text-left outline-none transition-colors hover:bg-foreground/[0.03] active:bg-foreground/[0.06] focus-visible:shadow-neon-cyan"
      >
        <span className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-light text-muted-foreground">
            {header}
          </span>
          <span aria-hidden className="shrink-0 text-xs font-light text-muted-quiet">
            {formatRoomTime(row.ts)}
          </span>
        </span>
        {body && (
          <span
            data-feedback-redact="true"
            className="line-clamp-2 text-base font-light text-foreground/90"
          >
            {body}
          </span>
        )}
      </button>
    </li>
  );
}

function ActivityEmpty() {
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-6 text-center">
      <p className="text-sm font-light text-muted-foreground">{mobileStrings.activity.empty}</p>
    </div>
  );
}
